// Salon Girişi — the hall door on event night. The host scans a guest's
// invitation QR, sees the party size and exactly which seats (with an
// orientation map), escorts them, and marks them entered. Optimised for a
// rapid scan → read → admit → next loop.

import { useSeatmap } from '@/features/booking/api/useSeatmap.ts';
import { MVP_EVENT_ID, trDot } from '@okn/shared';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  CheckCheck,
  DoorOpen,
  FileText,
  Minus,
  Plus,
  RotateCcw,
  ScanLine,
  Search,
  TriangleAlert,
  UserCheck,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadAdminFile } from './auth.ts';
import { admitGuests, useCheckInSummary } from './lib/checkInStore.ts';
import { parseScan } from './lib/format.ts';
import type { ReservationDetailData } from './lib/types.ts';
import {
  findByReference,
  useReservationDetail,
  useReservationSearch,
} from './lib/useReservation.ts';
import { StatusBadge } from './ui/Badge.tsx';
import { Button } from './ui/Button.tsx';
import { EmptyState } from './ui/EmptyState.tsx';
import { MiniSeatMap } from './ui/MiniSeatMap.tsx';
import { Modal } from './ui/Modal.tsx';
import { QRScanner } from './ui/QRScanner.tsx';
import { SeatList } from './ui/SeatList.tsx';
import { Spinner } from './ui/Spinner.tsx';
import { useToast } from './ui/Toast.tsx';

export function SalonGirisi() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scanBusy, setScanBusy] = useState(false);
  const toast = useToast();
  const stats = useCheckInSummary();

  const { items, loading, hasQuery } = useReservationSearch(query);
  const { state, reload } = useReservationDetail(selectedId);
  const map = useSeatmap(MVP_EVENT_ID);

  const onScan = async (raw: string) => {
    const { reservationId, reference } = parseScan(raw);
    setScanBusy(true);
    try {
      if (reservationId) setSelectedId(reservationId);
      else if (reference) {
        const id = await findByReference(reference);
        if (id) setSelectedId(id);
        else toast.error('Davetiyeye ait rezervasyon bulunamadı');
      } else toast.error('Karekod okunamadı');
    } finally {
      setScanBusy(false);
      setScanOpen(false);
    }
  };

  const reset = () => {
    setSelectedId(null);
    setQuery('');
  };

  const [rosterBusy, setRosterBusy] = useState(false);
  const downloadRoster = async () => {
    setRosterBusy(true);
    try {
      await downloadAdminFile('/admin/roster.pdf', 'salon-giris-listesi.pdf');
    } catch {
      toast.error('Liste oluşturulamadı.');
    } finally {
      setRosterBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="kicker mb-2">Salon Girişi</p>
          <h1 className="font-display text-2xl font-medium tracking-tight">Davetiye karşılama</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<FileText className="h-4 w-4" />}
            onClick={downloadRoster}
            loading={rosterBusy}
            disabled={rosterBusy}
          >
            Giriş Listesi
          </Button>
          <div className="flex items-center gap-3 rounded-lg border border-border bg-bg-elevated px-4 py-2.5">
            <DoorOpen className="h-5 w-5 text-gold" />
            <div className="leading-tight">
              <p className="text-[10px] uppercase tracking-wider text-text-faint">Bugün giriş</p>
              <p className="font-display text-lg tnum">
                {stats.seats}
                <span className="ml-1 text-xs text-text-faint">koltuk · {stats.parties} grup</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {!selectedId ? (
          <motion.div
            key="scan"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="mt-8"
          >
            <button
              type="button"
              onClick={() => setScanOpen(true)}
              className="group flex w-full flex-col items-center gap-4 rounded-2xl border border-border-strong border-dashed bg-bg-elevated px-6 py-14 transition-colors hover:border-accent hover:bg-surface"
            >
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-accent-quiet text-accent transition-transform group-hover:scale-105">
                <ScanLine className="h-9 w-9" strokeWidth={1.6} />
              </span>
              <span className="font-display text-xl tracking-tight">Davetiye Karekodunu Tara</span>
              <span className="text-sm text-text-muted">
                Misafirin telefonundaki davetiyeyi kameraya gösterin
              </span>
            </button>

            <div className="mt-6">
              <div className="relative">
                <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 text-text-faint" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  inputMode="search"
                  placeholder="Karekod yoksa telefon veya referans ile arayın"
                  className="input h-12 pl-10"
                />
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-text-faint">
                  <Spinner /> Aranıyor…
                </div>
              )}
              {!loading && hasQuery && items.length === 0 && (
                <p className="py-6 text-center text-sm text-text-faint">Eşleşen rezervasyon yok.</p>
              )}
              {!loading && items.length > 0 && (
                <div className="mt-3 space-y-2">
                  {items.map((item) => {
                    const ent = item.enteredCount ?? 0;
                    const full = ent > 0 && ent >= item.seatCount;
                    const partial = ent > 0 && ent < item.seatCount;
                    return (
                      <button
                        type="button"
                        key={item.id}
                        onClick={() => setSelectedId(item.id)}
                        className="flex w-full items-center gap-4 rounded-lg border border-border bg-bg-elevated px-4 py-3 text-left transition-colors hover:border-border-strong hover:bg-surface"
                      >
                        <span className="mono shrink-0 text-sm text-accent tnum">
                          {item.referenceCode}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm text-text">
                          {item.fullName}
                        </span>
                        <span className="mono text-xs text-text-muted tnum">
                          {item.seatCount} koltuk
                        </span>
                        {full ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-success">
                            <CheckCheck className="h-3.5 w-3.5" /> girdi
                          </span>
                        ) : partial ? (
                          <span className="inline-flex items-center gap-1 rounded-[5px] bg-gold-quiet px-1.5 py-0.5 text-[11px] font-medium text-gold tnum">
                            {ent}/{item.seatCount} girdi
                          </span>
                        ) : (
                          <StatusBadge status={item.status} />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="escort"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="mt-6"
          >
            <button
              type="button"
              onClick={reset}
              className="mb-4 inline-flex items-center gap-1.5 text-sm text-text-muted transition-colors hover:text-text"
            >
              <ArrowLeft className="h-4 w-4" /> Sonraki misafir
            </button>

            {state.status === 'loading' && (
              <div className="flex items-center justify-center gap-2 py-24 text-sm text-text-faint">
                <Spinner /> Yükleniyor…
              </div>
            )}
            {state.status === 'error' && (
              <EmptyState title="Açılamadı" description={state.message} />
            )}
            {state.status === 'ready' && (
              <EscortCard
                data={state.data}
                allSeats={map.status === 'ready' ? map.data.allSeats : []}
                reload={reload}
                onAdmitted={stats.refresh}
                onDone={reset}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <Modal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        title="Davetiye Tara"
        subtitle="Misafirin davetiye karekodunu kameraya gösterin"
        size="lg"
      >
        <div className="p-5">
          <QRScanner active={scanOpen} onResult={onScan} />
          {scanBusy && (
            <p className="mt-3 flex items-center justify-center gap-2 text-sm text-text-muted">
              <Spinner /> Rezervasyon bulunuyor…
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

function EscortCard({
  data,
  allSeats,
  reload,
  onAdmitted,
  onDone,
}: {
  data: ReservationDetailData;
  allSeats: import('@okn/shared').SeatDto[];
  reload: () => void;
  onAdmitted: () => void;
  onDone: () => void;
}) {
  const r = data.reservation;
  const navigate = useNavigate();
  const toast = useToast();

  const total = r.seatCount;
  const entered = r.enteredCount ?? 0;
  const remaining = Math.max(0, total - entered);
  const eligible = r.status === 'paid';
  const fullyIn = total > 0 && entered >= total;

  const [admitN, setAdmitN] = useState(() => Math.max(1, remaining));
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<null | { admitted: number; entered: number; total: number }>(
    null,
  );

  const highlightIds = useMemo(() => new Set(data.seats.map((s) => s.id)), [data.seats]);
  const clampN = (n: number) => Math.max(1, Math.min(remaining, n));

  // Admit `admitN` people now; the server caps at the party size. After a
  // successful admit we advance to the next guest — the rest of a partial
  // party come back on a later scan, which reloads the fresh count.
  const admit = async () => {
    if (busy || remaining === 0) return;
    setBusy(true);
    try {
      const res = await admitGuests(r.id, admitN);
      onAdmitted();
      setFlash({
        admitted: res.enteredCount - entered,
        entered: res.enteredCount,
        total: res.seatCount,
      });
      setTimeout(() => {
        setFlash(null);
        onDone();
      }, 1900);
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Giriş kaydedilemedi.');
      setBusy(false);
    }
  };

  // Reset this party's entry back to 0 (e.g. admitted the wrong group).
  const resetEntry = async () => {
    if (busy || entered === 0) return;
    setBusy(true);
    try {
      await admitGuests(r.id, -entered);
      onAdmitted();
      reload();
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Geri alınamadı.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-bg-elevated">
      {/* Success flash overlay */}
      <AnimatePresence>
        {flash && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-bg-elevated/95 backdrop-blur"
          >
            <motion.span
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 320, damping: 18 }}
              className="flex h-24 w-24 items-center justify-center rounded-full bg-success/15 text-success"
            >
              <CheckCheck className="h-12 w-12" strokeWidth={2} />
            </motion.span>
            <p className="mt-4 font-display text-2xl tracking-tight">
              {flash.entered >= flash.total
                ? 'Tamamı salona alındı'
                : `${flash.admitted} kişi alındı`}
            </p>
            <p className="mt-1 text-sm text-text-muted">
              {r.fullName} · {flash.entered}/{flash.total} içeride
              {flash.entered < flash.total ? ` · ${flash.total - flash.entered} bekliyor` : ''}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero: party size */}
      <div className="grid gap-px bg-border sm:grid-cols-[200px_1fr]">
        <div className="flex flex-col justify-center bg-bg-elevated px-6 py-7">
          <p className="kicker">Misafir</p>
          <p className="mt-1 font-display text-7xl leading-none tnum">{total}</p>
          <p className="mt-2 text-xs text-text-faint">
            koltuk{entered > 0 && !fullyIn ? ` · ${entered} içeride` : ''}
          </p>
        </div>
        <div className="flex flex-col justify-center bg-bg-elevated px-6 py-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mono text-sm text-accent tnum">{r.referenceCode}</span>
            <StatusBadge status={r.status} />
            {entered > 0 && !fullyIn && (
              <span className="rounded-[5px] bg-gold-quiet px-2 py-0.5 text-[11px] font-medium text-gold tnum">
                {entered}/{total} içeride · {remaining} bekliyor
              </span>
            )}
            {fullyIn && (
              <span className="inline-flex items-center gap-1 rounded-[5px] bg-[rgba(106,168,79,0.15)] px-2 py-0.5 text-[11px] font-medium text-success">
                <CheckCheck className="h-3.5 w-3.5" /> Tümü içeride
              </span>
            )}
          </div>
          <h2 className="mt-2 font-display text-2xl tracking-tight">{r.fullName}</h2>
          {r.guardianName && (
            <p className="mt-0.5 text-sm text-text-muted">Veli: {r.guardianName}</p>
          )}
          {entered > 0 && r.enteredAt && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-text-muted">
              <CheckCheck className="h-4 w-4 text-success" />
              Son giriş ·{' '}
              {trDot(
                new Date(r.enteredAt).toLocaleTimeString('tr-TR', {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              )}
            </p>
          )}
        </div>
      </div>

      {/* Not-yet-approved guard */}
      {!eligible && (
        <div className="flex items-start gap-3 border-t border-border bg-accent-faint px-6 py-4">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
          <div className="flex-1">
            <p className="text-sm text-text">
              {r.status === 'reserved'
                ? 'Bu rezervasyon henüz onaylanmamış. Misafiri salona almadan önce Onay Masası’nda ödemeyi onaylayın.'
                : 'Bu rezervasyon iptal edilmiş — salona giriş yapılamaz.'}
            </p>
            {r.status === 'reserved' && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => navigate(`/admin?focus=${r.id}`)}
              >
                Onay Masası’na git
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Seats + orientation map */}
      <div className="border-t border-border px-6 py-5">
        <SeatList seats={data.seats} size="lg" />
        {allSeats.length > 0 && (
          <div className="mt-5">
            <p className="kicker mb-2.5">Konum</p>
            <MiniSeatMap allSeats={allSeats} highlightIds={highlightIds} />
          </div>
        )}
      </div>

      {/* Action */}
      {eligible && (
        <div className="border-t border-border px-6 py-5">
          {remaining > 0 ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {total > 1 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-text-muted">Kaç kişi giriyor?</span>
                  <div className="flex items-center gap-1 rounded-lg border border-border bg-surface p-1">
                    <button
                      type="button"
                      aria-label="Azalt"
                      onClick={() => setAdmitN((n) => clampN(n - 1))}
                      disabled={admitN <= 1}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-bg-elevated hover:text-text disabled:opacity-35"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-10 text-center font-display text-xl tnum">{admitN}</span>
                    <button
                      type="button"
                      aria-label="Artır"
                      onClick={() => setAdmitN((n) => clampN(n + 1))}
                      disabled={admitN >= remaining}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-bg-elevated hover:text-text disabled:opacity-35"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <span className="text-xs text-text-faint tnum">/ {remaining} bekliyor</span>
                </div>
              )}
              <div className="flex items-center gap-2 sm:ml-auto">
                {entered > 0 && (
                  <Button
                    variant="ghost"
                    size="md"
                    icon={<RotateCcw className="h-4 w-4" />}
                    onClick={resetEntry}
                    disabled={busy}
                  >
                    Sıfırla
                  </Button>
                )}
                <Button
                  variant="gold"
                  size="xl"
                  icon={<DoorOpen className="h-5 w-5" />}
                  className="flex-1 sm:flex-none"
                  loading={busy}
                  disabled={busy}
                  onClick={admit}
                >
                  {total > 1 ? `${admitN} kişiyi salona al` : 'Salona Al'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2 text-sm text-success">
                <UserCheck className="h-5 w-5" /> Tamamı salonda ({total} kişi)
              </span>
              <div className="ml-auto flex gap-2">
                <Button
                  variant="ghost"
                  size="md"
                  icon={<RotateCcw className="h-4 w-4" />}
                  onClick={resetEntry}
                  disabled={busy}
                >
                  Geri al
                </Button>
                <Button variant="secondary" size="md" onClick={onDone}>
                  Sonraki
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
